/* Lost Culture Studio — poster size table + frame swatches.
   Works alongside theme.js: the hidden Gelato options stay the source of truth,
   this file only sets them and keeps labels/prices in sync. */
(() => {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const strings = () => (window.theme && window.theme.strings) || {};

  function dataFor(wrapper) {
    if (wrapper._lcsData !== undefined) return wrapper._lcsData;
    const json = $('[data-product-json]', wrapper);
    try { wrapper._lcsData = json ? JSON.parse(json.textContent) : null; } catch (e) { wrapper._lcsData = null; }
    return wrapper._lcsData;
  }

  function selected(wrapper) {
    return $$('[data-option-index]', wrapper).map((group) => {
      const checked = $('[data-option-input]:checked', group);
      return checked ? checked.value : null;
    });
  }

  function findVariant(data, options) {
    if (!data) return null;
    return data.variants.find((v) => v.options.every((val, i) => val === options[i])) || null;
  }

  function currentVariant(wrapper) {
    const data = dataFor(wrapper);
    const idInput = $('[data-variant-id]', wrapper);
    if (!data || !idInput) return null;
    return data.variants.find((v) => String(v.id) === String(idInput.value)) || findVariant(data, selected(wrapper));
  }

  /* Which variants count as unframed, based on the frame swatches present */
  function unframedTest(wrapper) {
    const virtual = $('[data-virtual-frame]', wrapper);
    if (virtual) {
      const styleIdx = Number(virtual.dataset.styleIdx);
      const values = $$('[data-frame-choice][data-unframed]', virtual).map((i) => i.dataset.styleValue);
      return (v) => values.includes(v.options[styleIdx]);
    }
    const native = $('.lcs-frame[data-option-index]', wrapper);
    if (native) {
      const idx = Number(native.dataset.optionIndex);
      const values = $$('[data-frame-swatch][data-unframed]', native).map((i) => i.value);
      return (v) => values.includes(v.options[idx]);
    }
    return null;
  }

  function refresh(wrapper) {
    const data = dataFor(wrapper);
    if (!data) return;
    const sel = selected(wrapper);

    /* Size rows: price for each size with the current frame choice */
    const sizeGroup = $('[data-lcs-size]', wrapper);
    let sizeIdx = -1;
    if (sizeGroup) {
      sizeIdx = Number(sizeGroup.dataset.optionIndex);
      $$('[data-option-input]', sizeGroup).forEach((input) => {
        const cand = sel.slice();
        cand[sizeIdx] = input.value;
        const v = findVariant(data, cand);
        const label = input.nextElementSibling;
        const priceEl = label && $('[data-size-price]', label);
        if (priceEl && v) priceEl.textContent = v.price_short || v.price;
      });
    }

    /* Frame label */
    const checkedSwatch = $('[data-frame-swatch]:checked', wrapper);
    const frameName = checkedSwatch ? checkedSwatch.dataset.frameName : '';
    const isUnframed = checkedSwatch ? checkedSwatch.hasAttribute('data-unframed') : true;
    $$('[data-frame-label]', wrapper).forEach((el) => { el.textContent = frameName; });

    /* "At this size: unframed $29 · framed $69" */
    const compare = $('[data-frame-compare]', wrapper);
    const isUnframedVariant = unframedTest(wrapper);
    if (compare && isUnframedVariant && sizeIdx > -1) {
      const sameSize = data.variants.filter((v) => v.options[sizeIdx] === sel[sizeIdx]);
      const unframed = sameSize.filter(isUnframedVariant).sort((a, b) => a.price_cents - b.price_cents)[0];
      const framed = sameSize.filter((v) => !isUnframedVariant(v)).sort((a, b) => a.price_cents - b.price_cents)[0];
      if (unframed && framed) {
        compare.textContent = `At this size: unframed ${unframed.price_short} · framed ${framed.price_short}`;
        compare.hidden = false;
      } else {
        compare.hidden = true;
      }
    }

    /* Note under the button */
    const note = $('[data-frame-note]', wrapper);
    if (note) {
      note.textContent = isUnframed
        ? 'Unframed print. Choose a frame above to have it arrive framed.'
        : `Arrives framed in a ${frameName.toLowerCase()} frame.`;
    }

    updateButton(wrapper);
    updateScenes(wrapper, checkedSwatch);
  }

  /* ---------- Scene gallery: studio + room mockups follow the frame ---------- */
  let scenesData;
  function scenes() {
    if (scenesData !== undefined) return scenesData;
    const el = $('[data-lcs-scenes]');
    try { scenesData = el ? JSON.parse(el.textContent) : null; } catch (e) { scenesData = null; }
    return scenesData;
  }

  function frameKey(swatch) {
    if (!swatch) return null;
    if (swatch.hasAttribute('data-unframed')) return 'none';
    const n = (swatch.dataset.frameName || swatch.value || '').toLowerCase();
    if (n.includes('black')) return 'black';
    if (n.includes('white')) return 'white';
    if (n.includes('oak') || n.includes('wood') || n.includes('natural')) return 'wood';
    return null;
  }

  function swapImage(img, entry, animate) {
    if (!img || !entry) return;
    if (!animate) {
      img.srcset = entry.srcset; img.src = entry.src; img.alt = entry.alt || img.alt;
      return;
    }
    const next = img.cloneNode(false);
    next.removeAttribute('loading');
    next.srcset = entry.srcset;
    next.src = entry.src;
    next.alt = entry.alt || img.alt;
    next.classList.add('lcs-fade-in');
    const done = () => {
      img.after(next);
      requestAnimationFrame(() => requestAnimationFrame(() => next.classList.add('is-in')));
      setTimeout(() => { img.remove(); next.classList.remove('lcs-fade-in', 'is-in'); }, 320);
    };
    (next.decode ? next.decode() : Promise.resolve()).then(done, done);
  }

  function updateScenes(wrapper, swatch) {
    const data = scenes();
    if (!data || !wrapper.closest('.section-main-product')) return;
    const key = frameKey(swatch);
    if (!key) return;
    const gallery = $('.section-main-product .lcs-scenes');
    if (!gallery) return;
    const first = !gallery.dataset.lcsFrame;
    if (gallery.dataset.lcsFrame === key) return;
    gallery.dataset.lcsFrame = key;
    if (first) return; /* the page already rendered the right images */
    ['studio', 'room'].forEach((slot) => {
      const entry = data[`${slot}:${key}`];
      const imgs = $$(`.lcs-slide--${slot} .product__media-frame img`, gallery);
      const img = imgs[imgs.length - 1];
      if (entry && img) swapImage(img, entry, true);
    });
  }

  function updateButton(wrapper) {
    const label = $('[data-add-label]', wrapper);
    const btn = $('[data-add-button]', wrapper);
    if (!label || !btn || btn.disabled) return;
    const add = strings().addToCart;
    if (!add || label.textContent.trim() !== add) return;
    const v = currentVariant(wrapper);
    if (v) label.textContent = `${add} — ${v.price_short || v.price}`;
  }

  /* A frame swatch picks the matching hidden Gelato Product + Style values */
  function applyFrameChoice(wrapper, choice) {
    const virtual = choice.closest('[data-virtual-frame]');
    if (!virtual) return;
    const productGroup = $(`[data-option-index="${virtual.dataset.productIdx}"]`, wrapper);
    const styleGroup = $(`[data-option-index="${virtual.dataset.styleIdx}"]`, wrapper);
    if (!productGroup || !styleGroup) return;
    const pInput = $$('[data-option-input]', productGroup).find((i) => i.value === choice.dataset.productValue);
    const sInput = $$('[data-option-input]', styleGroup).find((i) => i.value === choice.dataset.styleValue);
    if (pInput) pInput.checked = true;
    if (sInput) {
      sInput.checked = true;
      sInput.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (pInput) {
      pInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /* Hidden options that follow the others (e.g. Gelato "Product" follows Frame):
     pick the value that makes a real variant, preferring available ones. */
  function syncAuto(wrapper) {
    const data = dataFor(wrapper);
    if (!data) return;
    $$('[data-auto-option]', wrapper).forEach((group) => {
      const idx = Number(group.dataset.optionIndex);
      const sel = selected(wrapper);
      const inputs = $$('[data-option-input]', group);
      let pick = null;
      inputs.forEach((input) => {
        const cand = sel.slice();
        cand[idx] = input.value;
        const v = findVariant(data, cand);
        if (v && (!pick || (v.available && !pick.v.available))) pick = { input, v };
      });
      if (pick) pick.input.checked = true;
    });
  }

  function init(wrapper) {
    if (!wrapper.classList.contains('lcs-picker') || wrapper.dataset.lcsReady) return;
    wrapper.dataset.lcsReady = 'true';
    const label = $('[data-add-label]', wrapper);
    if (label && 'MutationObserver' in window) {
      new MutationObserver(() => updateButton(wrapper)).observe(label, { childList: true, characterData: true, subtree: true });
    }
    syncAuto(wrapper);
    refresh(wrapper);
  }

  /* Capture phase: runs before theme.js reads the selected options */
  document.addEventListener('change', (e) => {
    const wrapper = e.target.closest && e.target.closest('.lcs-picker');
    if (!wrapper) return;
    if (e.target.matches('[data-frame-choice]')) {
      applyFrameChoice(wrapper, e.target);
      return;
    }
    if (e.target.matches('[data-option-input]')) {
      if (!e.target.closest('[data-auto-option]')) syncAuto(wrapper);
      /* let theme.js pick the variant first */
      setTimeout(() => refresh(wrapper), 0);
    }
  }, true);

  /* ---------- Product gallery: one image at a time with arrows ---------- */
  function galleryTrack() { return $('.section-main-product [data-gallery-track]'); }

  function updateGalleryNav() {
    const track = galleryTrack();
    if (!track) return;
    const gallery = track.closest('[data-gallery]');
    const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
    const last = track.children.length - 1;
    const prev = $('[data-lcs-gallery-step="-1"]', gallery);
    const next = $('[data-lcs-gallery-step="1"]', gallery);
    if (prev) prev.hidden = i <= 0;
    if (next) next.hidden = i >= last;
    const idx = $('[data-gallery-index]', gallery);
    if (idx) idx.textContent = i + 1;
  }

  function showMedia(mediaId, smooth) {
    const track = galleryTrack();
    if (!track || !mediaId) return;
    const item = $(`[data-media-id="${mediaId}"]`, track);
    if (!item) return;
    track.scrollTo({ left: item.offsetLeft - track.offsetLeft, behavior: 'instant' });
  }

  function initGallery() {
    const track = galleryTrack();
    if (!track || track.dataset.lcsGallery) return;
    track.dataset.lcsGallery = 'true';
    track.addEventListener('scroll', () => window.requestAnimationFrame(updateGalleryNav), { passive: true });
    window.addEventListener('resize', updateGalleryNav);
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-lcs-gallery-step]');
      if (!btn) return;
      const t = galleryTrack();
      if (!t) return;
      t.scrollBy({ left: Number(btn.dataset.lcsGalleryStep) * t.clientWidth, behavior: 'instant' });
    });
    /* Start on the selected variant's image */
    const featured = $('.product__media.is-featured', track);
    if (featured) showMedia(featured.dataset.mediaId, false);
    updateGalleryNav();
  }

  /* After a variant change, slide to that variant's image (theme.js only reorders on desktop) */
  document.addEventListener('change', (e) => {
    if (!e.target.closest || !e.target.closest('.section-main-product .product-form')) return;
    setTimeout(() => {
      const wrapper = e.target.closest('.product-form');
      const v = wrapper && currentVariant(wrapper);
      if (v && v.media_id) showMedia(v.media_id, true);
    }, 30);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initGallery);
  else initGallery();

  function initAll(root = document) { $$('.lcs-picker', root).forEach(init); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  /* Quick view and theme editor inject product forms later */
  if ('MutationObserver' in window) {
    new MutationObserver((mutations) => {
      mutations.forEach((m) => m.addedNodes.forEach((n) => {
        if (n.nodeType !== 1) return;
        if (n.matches && n.matches('.lcs-picker')) init(n);
        else if (n.querySelectorAll) initAll(n);
      }));
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
